/**
 * Configurações → Mensagens prontas.
 *
 * ── O que se escreve aqui ────────────────────────────────────────────────
 *
 * O texto que a equipe repete o dia inteiro: a saudação, o endereço, o
 * horário, a explicação da troca. Ele aparece no atendimento dentro da caixa
 * de mensagens prontas, na aba "Da organização", e entra no campo de texto
 * para ser editado antes de enviar.
 *
 * ── A forma é a das outras seções, e isso não é detalhe ─────────────────
 *
 * `admin__secao`, `admin__nota` e o formulário dentro de um `Cartao` folgado:
 * é o mesmo esqueleto de Etiquetas e de Etapas do funil. A primeira versão
 * desta tela inventou classes próprias e ficou com a cara errada — o campo de
 * texto sem largura, o formulário solto no meio do nada, e um aviso amarelo do
 * tamanho da tela para dizer algo que é explicação, não alarme.
 *
 * ── A lista vem ANTES do formulário ─────────────────────────────────────
 *
 * Quem abre esta tela quase sempre vem corrigir uma mensagem que já existe, e
 * não escrever a primeira. É a ordem que Etiquetas usa, pelo mesmo motivo.
 *
 * ── Uma lista com edição no lugar, e não um diálogo por item ────────────
 *
 * São textos curtos que se corrigem com frequência — uma vírgula, um telefone
 * que mudou. Abrir diálogo para cada correção transformaria dois segundos de
 * trabalho em quatro cliques.
 */

import { useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { MensagemPronta } from '@cliqcrm/contracts';
import { useApi, useMensagensProntas } from '@/shared/api/consultas';
import { chaves } from '@/shared/api/chaves';
import { Botao, Campo, CampoArea, Cartao } from '@/shared/ui';
import { Faixa } from '@/shared/ui/estados/faixa';
import { recadoDeErro } from '@/shared/api/recado-de-erro';

const TITULO_MAXIMO = 80;
const TEXTO_MAXIMO = 2_000;

export function SecaoMensagensProntas(): ReactNode {
  const api = useApi();
  const cache = useQueryClient();
  const { data, isPending, isError } = useMensagensProntas();

  const [titulo, definirTitulo] = useState('');
  const [texto, definirTexto] = useState('');

  const invalidar = (): void => {
    void cache.invalidateQueries({ queryKey: chaves.mensagensProntas() });
  };

  const criar = useMutation({
    mutationFn: async () => {
      if (!api) throw new Error('Sem sessão.');
      return api.criarMensagemPronta({ corpo: { titulo: titulo.trim(), texto: texto.trim() } });
    },
    onSuccess: () => {
      definirTitulo('');
      definirTexto('');
      invalidar();
    },
  });

  const podeCriar = titulo.trim() !== '' && texto.trim() !== '' && !criar.isPending;
  const itens = data?.itens ?? [];

  return (
    <section className="admin__secao" aria-label="Mensagens prontas">
      {/*
        A regra das 24 h é EXPLICAÇÃO, e por isso `admin__nota` e não faixa de
        atenção. Quem escreve a lista precisa saber que ela não serve para
        reabrir conversa parada — senão cadastra uma "retomada de contato" que o
        WhatsApp nunca entrega e conclui que o CliqCRM está com defeito. Mas
        isso é instrução, e o amarelo gasta a atenção que o alarme de verdade
        vai precisar.

        O texto também não repete o subtítulo da seção, que já diz o que estas
        mensagens são. Dizer duas vezes a mesma coisa em duas linhas seguidas
        ensina a pular a segunda.
      */}
      <p className="admin__nota">
        No atendimento, estes textos aparecem na caixa de mensagens prontas e entram no campo para
        serem ajustados antes de enviar. Valem <strong>dentro das 24 h</strong> desde a última
        mensagem do cliente; para falar com quem sumiu antes disso, o caminho é o modelo aprovado
        pela Meta, na outra aba da mesma caixa.
      </p>

      {isPending && <p className="admin__nota">Buscando as mensagens da organização…</p>}
      {isError && <Faixa tom="bloqueado">Não foi possível buscar as mensagens prontas.</Faixa>}

      {data && itens.length === 0 && (
        <p className="admin__nota">
          Nenhuma mensagem pronta ainda. A primeira costuma ser a saudação — é a que a equipe mais
          redigita.
        </p>
      )}

      {itens.length > 0 && (
        <ul className="admin__lista prontas__lista">
          {itens.map((mensagem) => (
            <LinhaDaMensagem key={mensagem.id} mensagem={mensagem} aoMudar={invalidar} />
          ))}
        </ul>
      )}

      <Cartao
        como="form"
        variante="folgado"
        parte="admin__form"
        onSubmit={(evento: FormEvent) => {
          evento.preventDefault();
          if (podeCriar) criar.mutate();
        }}
      >
        <Campo
          rotulo="Título"
          type="text"
          value={titulo}
          maxLength={TITULO_MAXIMO}
          placeholder="Saudação"
          ajuda="É por ele que a equipe encontra o texto na hora de responder."
          onChange={(evento) => definirTitulo(evento.currentTarget.value)}
        />
        <CampoArea
          rotulo="Texto"
          rows={3}
          value={texto}
          maxLength={TEXTO_MAXIMO}
          placeholder="Bom dia! Sou da Cliqfy. Em que posso ajudar?"
          onChange={(evento) => definirTexto(evento.currentTarget.value)}
        />

        {criar.isError && (
          <Faixa tom="bloqueado">
            {recadoDeErro(criar.error, { generica: 'Não foi possível criar a mensagem pronta.' })}
          </Faixa>
        )}

        <Botao
          type="submit"
          variante="primario"
          desabilitado={!podeCriar}
          motivo={criar.isPending ? 'Criando…' : 'Preencha título e texto.'}
        >
          {criar.isPending ? 'Criando…' : 'Criar mensagem'}
        </Botao>
      </Cartao>
    </section>
  );
}

/**
 * Uma mensagem da lista, com edição no lugar.
 *
 * O estado de edição é local à linha: duas pessoas da mesma equipe mexendo em
 * mensagens diferentes é caso comum, e um estado no topo faria abrir uma fechar
 * a outra.
 */
function LinhaDaMensagem({
  mensagem,
  aoMudar,
}: {
  mensagem: MensagemPronta;
  aoMudar: () => void;
}): ReactNode {
  const api = useApi();
  const [editando, definirEditando] = useState(false);
  const [titulo, definirTitulo] = useState(mensagem.titulo);
  const [texto, definirTexto] = useState(mensagem.texto);
  const [confirmando, definirConfirmando] = useState(false);

  const salvar = useMutation({
    mutationFn: async () => {
      if (!api) throw new Error('Sem sessão.');
      return api.editarMensagemPronta({
        caminho: { id: mensagem.id },
        corpo: { titulo: titulo.trim(), texto: texto.trim() },
      });
    },
    onSuccess: () => {
      definirEditando(false);
      aoMudar();
    },
  });

  const remover = useMutation({
    mutationFn: async () => {
      if (!api) throw new Error('Sem sessão.');
      return api.removerMensagemPronta({ caminho: { id: mensagem.id } });
    },
    onSuccess: aoMudar,
  });

  /* Uma faixa só: as duas ações não acontecem ao mesmo tempo, e duas caixas de
     erro empilhadas na mesma linha seria ruído. */
  const falha = salvar.isError
    ? recadoDeErro(salvar.error, { generica: 'Não foi possível salvar a correção.' })
    : remover.isError
      ? recadoDeErro(remover.error, { generica: 'Não foi possível remover.' })
      : null;

  return (
    <li>
      <Cartao variante="folgado" parte="prontas__item">
        {editando ? (
          <>
            <Campo
              rotulo="Título"
              type="text"
              value={titulo}
              maxLength={TITULO_MAXIMO}
              autoFocus
              onChange={(evento) => definirTitulo(evento.currentTarget.value)}
            />
            <CampoArea
              rotulo="Texto"
              rows={3}
              value={texto}
              maxLength={TEXTO_MAXIMO}
              onChange={(evento) => definirTexto(evento.currentTarget.value)}
            />
            {falha && <Faixa tom="bloqueado">{falha}</Faixa>}
            <div className="prontas__acoes">
              <Botao
                variante="primario"
                onClick={() => salvar.mutate()}
                desabilitado={titulo.trim() === '' || texto.trim() === '' || salvar.isPending}
                motivo={salvar.isPending ? 'Salvando…' : 'Título e texto não podem ficar vazios.'}
              >
                {salvar.isPending ? 'Salvando…' : 'Salvar'}
              </Botao>
              <Botao
                onClick={() => {
                  // Volta ao que está gravado: sair da edição sem desfazer
                  // deixaria o texto alterado na tela sem estar salvo em lugar
                  // nenhum.
                  definirTitulo(mensagem.titulo);
                  definirTexto(mensagem.texto);
                  definirEditando(false);
                }}
              >
                Cancelar
              </Botao>
            </div>
          </>
        ) : (
          <>
            <div className="prontas__cabecalho">
              <strong className="prontas__titulo">{mensagem.titulo}</strong>
              <div className="prontas__acoes">
                <Botao miudo onClick={() => definirEditando(true)}>
                  Editar
                </Botao>
                {/*
                  A remoção pede confirmação NA PRÓPRIA linha.

                  Não é diálogo porque o risco é pequeno — o texto se reescreve
                  —, e não é clique único porque a lista é de toda a equipe:
                  quem apaga não é quem vai sentir falta.
                */}
                {confirmando ? (
                  <>
                    <Botao
                      miudo
                      variante="perigo"
                      onClick={() => remover.mutate()}
                      desabilitado={remover.isPending}
                      motivo="Removendo…"
                    >
                      Confirmar
                    </Botao>
                    <Botao miudo onClick={() => definirConfirmando(false)}>
                      Não
                    </Botao>
                  </>
                ) : (
                  <Botao miudo onClick={() => definirConfirmando(true)}>
                    Remover
                  </Botao>
                )}
              </div>
            </div>
            <p className="prontas__texto">{mensagem.texto}</p>
            {falha && <Faixa tom="bloqueado">{falha}</Faixa>}
          </>
        )}
      </Cartao>
    </li>
  );
}
